/**
 * aethereum reconnect — recover this project's room identity WITHOUT minting a
 * new room.
 *
 * Older configs stored only { url, token } for a project, so commands that need
 * the roomId (`agent add`, `claim`, `rotate`) dead-ended with "re-run init",
 * which would create a brand-new room and orphan the old one. Reconnect uses the
 * existing project token against GET /api/rooms (a room token sees exactly its
 * own room) to recover the roomId + anon flag, then saves them via saveProject.
 * Nothing is created server-side; this is a pure read + local config heal.
 *
 * Fails soft: offline / invalid token prints a clear message and exits 1.
 */

import { homedir } from "node:os";
import { AccountClient, OfflineError } from "../hive/account-client.js";
import { resolveProject, readConfig, saveProject } from "../hive/config-store.js";

export interface ReconnectOptions {
  url?: string;
  /** Override the room token (else read from the active project config). */
  token?: string;
  home?: string;
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

const DEFAULT_BASE = "https://www.aethereum.dev";

export async function runReconnect(options: ReconnectOptions = {}): Promise<void> {
  const home = options.home ?? homedir();
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const project = resolveProject(cwd, home);

  const token = options.token ?? env["AETHEREUM_SYNC_TOKEN"] ?? project?.token;
  const base = (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? project?.url ?? DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");

  if (!token) {
    console.error(
      "No room token found for this directory. If you have one, run 'aethereum reconnect --token <token>'; otherwise run 'aethereum init'.",
    );
    process.exit(1);
    return;
  }

  const client = new AccountClient(base, options.fetchImpl ?? fetch);
  try {
    const rooms = await client.listRooms(token);
    const room = rooms[0];
    if (!room?.id) {
      console.error(
        "The server did not recognize this token as a room token. Reconnect from the dashboard, or run 'aethereum init --new-room'.",
      );
      process.exit(1);
      return;
    }

    // Save under the resolved project path when one exists (heals an ancestor
    // entry in place); else register THIS directory. Merge to keep local prefs.
    const projectPath = project?.projectPath && project.projectPath !== "default" ? project.projectPath : cwd;
    let prev: Partial<import("../hive/config-store.js").ProjectConfig> = {};
    try {
      prev = readConfig(home).projects[projectPath] ?? {};
    } catch {
      prev = {};
    }
    saveProject(
      projectPath,
      { ...prev, url: base, token, roomId: room.id, anon: Boolean(room.anon) },
      home,
    );

    console.log(`\n  [ok] reconnected: room ${room.id}${room.name ? ` ("${room.name}")` : ""} -- ${room.anon ? "anon (unclaimed)" : "claimed"}`);
    console.log(`       saved for ${projectPath}\n`);
    console.log("  'aethereum agent add', 'aethereum claim', and 'aethereum rotate' now work here.\n");
  } catch (err) {
    if (err instanceof OfflineError) {
      console.error("Could not reach Aethereum (offline?). Try again when you are online.");
    } else {
      console.error(err instanceof Error ? err.message : String(err));
    }
    process.exit(1);
  }
}
